/**
 * The durable {@link JournalFileStore}: the Journal's bytes on the Origin
 * Private File System.
 *
 * `MemoryJournalFileStore` is the behavioural specification and this is the
 * substrate that ships. Everything transactional — backup, verify, rollback,
 * bundle, recover — already lives above the interface in code that never
 * touches a platform, so the only thing this file owes the repository is that
 * every operation means *exactly* what it means in memory, including which
 * failures throw which repository error code. `opfsFiles.test.ts` runs one
 * suite against both implementations for that reason: a difference here is
 * invisible until a real user's Journal is the thing being enumerated.
 *
 * `@ff/store` is the one package allowed to reach a browser storage API
 * directly (`03-ENGINEERING-SEAMS.md` §1.1), and the reach is kept to a single
 * function — {@link openOpfsJournalFileStore} — so the class itself takes a
 * root *handle*. That is not a testing convenience: OPFS has no in-process
 * implementation under jsdom, and a store that resolved its own root could
 * only ever be proven in a browser.
 *
 * Three OPFS facts shape the code below:
 *
 *  1. **There are no paths, only handles.** Every operation walks the path
 *     segment by segment from the root, which is why `create` is threaded down
 *     the walk rather than checked once at the end.
 *  2. **A lookup reports the wrong *kind* as an error, not as a miss.** Asking
 *     for a file where a directory sits raises `TypeMismatchError`. For a
 *     lookup that is the same fact as absence — there is no file here — so both
 *     names are treated as absent, and that is what keeps `read` of a directory
 *     answering `missing` exactly as the memory store does.
 *  3. **`move` is not portable.** `FileSystemFileHandle.move()` exists in
 *     recent Chrome and nowhere else, so the fallback is copy-then-delete —
 *     ordered so that a copy which fails, or lands bytes that do not verify,
 *     leaves the source exactly where it was.
 */

import type { JournalFileRead, JournalFileStore } from "./files";
import {
  JournalFileError,
  journalBytesEqual,
  journalPathName,
  journalPathParent,
} from "./files";

/* ------------------------------------------------------------------------- *
 * The handle surface
 * ------------------------------------------------------------------------- */

/**
 * The slice of the File System Access API this store uses, declared here
 * rather than taken from `lib.dom`.
 *
 * Two reasons, and neither is stylistic. The workspace's `lib` does not include
 * `DOM.AsyncIterable`, so the standard `FileSystemDirectoryHandle` has no typed
 * `keys()` — enumeration, the one operation the fail-closed contract turns on,
 * would be untyped. And a structural interface is what lets the tests drive a
 * shim: OPFS does not exist under jsdom, so the alternative to injecting a root
 * handle is proving none of this at all.
 */
export interface OpfsFileSnapshot {
  arrayBuffer(): Promise<ArrayBuffer>;
}

export interface OpfsWritableFile {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
  /** Discards the pending contents. Absent on older implementations. */
  abort?: () => Promise<void>;
}

export interface OpfsFileHandle {
  readonly kind: "file";
  getFile(): Promise<OpfsFileSnapshot>;
  createWritable(): Promise<OpfsWritableFile>;
  /**
   * Chrome-only relocation. Optional because the fallback has to exist anyway,
   * and a store that assumed this method would fail on Safari at the first
   * quarantine — the exact moment a damaged record is being preserved.
   */
  move?: (parent: OpfsDirectoryHandle, name: string) => Promise<void>;
}

export interface OpfsDirectoryHandle {
  readonly kind: "directory";
  getDirectoryHandle(
    name: string,
    options?: { readonly create?: boolean },
  ): Promise<OpfsDirectoryHandle>;
  getFileHandle(
    name: string,
    options?: { readonly create?: boolean },
  ): Promise<OpfsFileHandle>;
  removeEntry(
    name: string,
    options?: { readonly recursive?: boolean },
  ): Promise<void>;
  keys(): AsyncIterable<string>;
}

/* ------------------------------------------------------------------------- *
 * Error shaping
 * ------------------------------------------------------------------------- */

/**
 * Lookup failures that mean "nothing of that kind is here".
 *
 * `TypeMismatchError` belongs with `NotFoundError` because a *lookup* asking
 * for a file cannot use a directory of the same name, and the memory store
 * answers both with a plain miss. Treating the mismatch as an I/O failure would
 * turn `read` of a directory path into an unavailable Journal.
 */
const ABSENT_ERROR_NAMES: ReadonlySet<string> = new Set([
  "NotFoundError",
  "TypeMismatchError",
]);

function errorName(error: unknown): string {
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { readonly name: unknown }).name;
    if (typeof name === "string") return name;
  }
  return "";
}

function isAbsence(error: unknown): boolean {
  return ABSENT_ERROR_NAMES.has(errorName(error));
}

function detailOf(error: unknown): string {
  if (error instanceof Error) {
    return error.name.length > 0 ? `${error.name}: ${error.message}` : error.message;
  }
  return String(error);
}

/**
 * Wraps a substrate failure, preserving a {@link JournalFileError} that already
 * carries a deliberately chosen code.
 *
 * Re-coding an error that was raised on purpose one frame down is how a
 * `deletionNotVerified` becomes a generic `ioFailure` and the repository takes
 * the wrong branch.
 */
function fileError(
  code: JournalFileError["code"],
  path: string,
  message: string,
  cause: unknown,
): JournalFileError {
  if (cause instanceof JournalFileError) return cause;
  return new JournalFileError(code, path, `${message}: ${detailOf(cause)}`);
}

/** Path segments with empties dropped, so a root of `""` walks to nothing. */
function pathSegments(path: string): readonly string[] {
  return path.split("/").filter((segment) => segment.length > 0);
}

/* ------------------------------------------------------------------------- *
 * The store
 * ------------------------------------------------------------------------- */

export class OpfsJournalFileStore implements JournalFileStore {
  readonly #root: OpfsDirectoryHandle;

  constructor(root: OpfsDirectoryHandle) {
    this.#root = root;
  }

  async createDirectory(path: string): Promise<void> {
    try {
      await this.#walk(path, true);
    } catch (error) {
      throw fileError("ioFailure", path, "could not create directory", error);
    }
  }

  async directoryExists(path: string): Promise<boolean> {
    try {
      return (await this.#walk(path, false)) !== undefined;
    } catch (error) {
      // Not `false`. A directory that cannot be *asked about* is not a
      // directory that is absent, and the repository treats a false here as
      // `repositoryUnavailable` — which is the safe conclusion — only if it
      // gets to see the failure at all.
      throw fileError("ioFailure", path, "could not resolve directory", error);
    }
  }

  async list(directory: string): Promise<readonly string[]> {
    let handle: OpfsDirectoryHandle | undefined;
    try {
      handle = await this.#walk(directory, false);
    } catch (error) {
      throw fileError(
        "enumerationFailed",
        directory,
        "could not resolve directory",
        error,
      );
    }
    if (handle === undefined) {
      // An absent directory is not an empty one — the substitution the whole
      // fail-closed contract forbids, and the reason F-JP-19 enumerates every
      // Journal directory on every availability pass.
      throw new JournalFileError(
        "enumerationFailed",
        directory,
        "no such directory",
      );
    }
    const names: string[] = [];
    try {
      for await (const name of handle.keys()) names.push(name);
    } catch (error) {
      // The directory resolved and then would not list: partial results are
      // worse than none, because a truncated listing reads as a Journal that
      // lost records.
      throw fileError(
        "enumerationFailed",
        directory,
        "could not enumerate directory",
        error,
      );
    }
    return names;
  }

  async fileExists(path: string): Promise<boolean> {
    try {
      return (await this.#existingFile(path)) !== undefined;
    } catch (error) {
      throw fileError("ioFailure", path, "could not resolve file", error);
    }
  }

  async read(path: string): Promise<JournalFileRead> {
    let handle: OpfsFileHandle | undefined;
    try {
      handle = await this.#existingFile(path);
    } catch (error) {
      return { kind: "failed", detail: detailOf(error) };
    }
    if (handle === undefined) return { kind: "missing" };
    try {
      const snapshot = await handle.getFile();
      return { kind: "bytes", bytes: new Uint8Array(await snapshot.arrayBuffer()) };
    } catch (error) {
      // A file removed between resolving the handle and reading it is missing,
      // not damaged: reporting `failed` would quarantine a record that simply
      // is not there any more.
      if (isAbsence(error)) return { kind: "missing" };
      return { kind: "failed", detail: detailOf(error) };
    }
  }

  async write(path: string, bytes: Uint8Array): Promise<void> {
    try {
      const handle = await this.#fileForWriting(path);
      const writable = await handle.createWritable();
      try {
        await writable.write(bytes);
      } catch (error) {
        // Closing would commit whatever landed. Aborting is the only way to
        // leave the previous contents intact, and the verified write above
        // this layer depends on a failed write not having half-replaced a
        // record it is about to roll back to.
        await abortQuietly(writable);
        throw error;
      }
      await writable.close();
    } catch (error) {
      throw fileError("ioFailure", path, "could not write file", error);
    }
  }

  async remove(path: string): Promise<void> {
    const name = journalPathName(path);
    if (pathSegments(path).length === 0) return;
    try {
      const parent = await this.#walk(journalPathParent(path), false);
      if (parent === undefined) return;
      // `removeEntry` deletes a *directory* of the same name just as happily,
      // and the memory store's `remove` only ever touches files. Confirming the
      // kind first is what keeps a mistaken path from deleting a Journal
      // directory instead of failing to find a record.
      const file = await lookupFile(parent, name);
      if (file === undefined) return;
      await parent.removeEntry(name);
    } catch (error) {
      throw fileError("ioFailure", path, "could not remove file", error);
    }
  }

  async removeDirectory(path: string): Promise<void> {
    const name = journalPathName(path);
    if (pathSegments(path).length === 0) {
      // Recursively removing the root would take the Journal with it. The
      // memory store cannot express this path either; refusing is the only
      // behaviour that is the same in both.
      return;
    }
    try {
      const parent = await this.#walk(journalPathParent(path), false);
      if (parent === undefined) return;
      const directory = await lookupDirectory(parent, name);
      if (directory === undefined) return;
      await parent.removeEntry(name, { recursive: true });
    } catch (error) {
      throw fileError("ioFailure", path, "could not remove directory", error);
    }
  }

  async move(from: string, to: string): Promise<void> {
    if (from === to) {
      // A self-move must not run the copy-then-delete path: the delete would
      // remove the file the copy just wrote over itself, destroying a record
      // whose only sin was already being where it belongs.
      return;
    }
    const name = journalPathName(to);
    if (pathSegments(to).length === 0) {
      throw new JournalFileError("ioFailure", to, "cannot move onto the root");
    }

    let source: OpfsFileHandle | undefined;
    try {
      source = await this.#existingFile(from);
    } catch (error) {
      throw fileError("ioFailure", from, "could not resolve file to move", error);
    }
    if (source === undefined) {
      throw new JournalFileError("ioFailure", from, "no such file to move");
    }

    let destinationParent: OpfsDirectoryHandle;
    try {
      destinationParent = await this.#ensureDirectory(journalPathParent(to));
    } catch (error) {
      throw fileError("ioFailure", to, "could not create destination", error);
    }

    if (typeof source.move === "function") {
      try {
        await source.move(destinationParent, name);
        return;
      } catch {
        // Fall through. The common cause is an implementation that will not
        // relocate onto an existing destination, and the contract says the
        // destination is replaced — so the copy path finishes the job rather
        // than reporting a failure the caller cannot act on.
      }
    }

    await this.#copyThenDelete(from, to);
  }

  /* --- internals --------------------------------------------------------- */

  /**
   * Walks a path to a directory handle.
   *
   * `undefined` is returned only when `create` is false and a segment reported
   * absence; with `create` true every failure is a real one and propagates,
   * because a directory that could not be created is not a directory that is
   * missing.
   */
  async #walk(
    path: string,
    create: boolean,
  ): Promise<OpfsDirectoryHandle | undefined> {
    let handle = this.#root;
    for (const segment of pathSegments(path)) {
      try {
        handle = await handle.getDirectoryHandle(segment, { create });
      } catch (error) {
        if (!create && isAbsence(error)) return undefined;
        throw error;
      }
    }
    return handle;
  }

  /**
   * The creating walk, with the absence case turned into a failure.
   *
   * A create walk cannot report absence — every segment is made on the way
   * down — so a substrate that answers with nothing has told us the directory
   * is not there *and* was not created, which is a failure, not a miss.
   */
  async #ensureDirectory(path: string): Promise<OpfsDirectoryHandle> {
    const handle = await this.#walk(path, true);
    if (handle === undefined) {
      throw new JournalFileError(
        "ioFailure",
        path,
        "directory did not resolve after being created",
      );
    }
    return handle;
  }

  /** The file at `path`, or `undefined` when nothing of that kind is there. */
  async #existingFile(path: string): Promise<OpfsFileHandle | undefined> {
    if (pathSegments(path).length === 0) return undefined;
    const parent = await this.#walk(journalPathParent(path), false);
    if (parent === undefined) return undefined;
    return lookupFile(parent, journalPathName(path));
  }

  /** The file at `path`, creating it and every missing parent. */
  async #fileForWriting(path: string): Promise<OpfsFileHandle> {
    if (pathSegments(path).length === 0) {
      throw new JournalFileError("ioFailure", path, "cannot write the root");
    }
    const parent = await this.#ensureDirectory(journalPathParent(path));
    return parent.getFileHandle(journalPathName(path), { create: true });
  }

  /**
   * The portable `move`: copy, verify, and only then delete the source.
   *
   * The ordering is the whole point. A record being quarantined (F-JP-23) is
   * already damaged; losing it to a copy that failed halfway would turn a
   * preservable record into a destroyed one, so the source stays until the
   * destination has been read back and compared byte for byte.
   */
  async #copyThenDelete(from: string, to: string): Promise<void> {
    const source = await this.read(from);
    if (source.kind !== "bytes") {
      const detail = source.kind === "missing" ? "no such file to move" : source.detail;
      throw new JournalFileError("ioFailure", from, detail);
    }

    await this.write(to, source.bytes);

    const written = await this.read(to);
    if (written.kind !== "bytes" || !journalBytesEqual(written.bytes, source.bytes)) {
      // The destination is left as it is. Deleting it is one more write that
      // can fail, and the caller's retry replaces it anyway; what matters is
      // that the source is still here.
      throw new JournalFileError(
        "ioFailure",
        to,
        "moved file did not verify at the destination",
      );
    }

    await this.remove(from);
  }
}

/** Resolves a child file, mapping "not a file here" to `undefined`. */
async function lookupFile(
  parent: OpfsDirectoryHandle,
  name: string,
): Promise<OpfsFileHandle | undefined> {
  try {
    return await parent.getFileHandle(name);
  } catch (error) {
    if (isAbsence(error)) return undefined;
    throw error;
  }
}

/** Resolves a child directory, mapping "not a directory here" to `undefined`. */
async function lookupDirectory(
  parent: OpfsDirectoryHandle,
  name: string,
): Promise<OpfsDirectoryHandle | undefined> {
  try {
    return await parent.getDirectoryHandle(name);
  } catch (error) {
    if (isAbsence(error)) return undefined;
    throw error;
  }
}

/**
 * Best-effort abort.
 *
 * The failure that brought us here is the one worth reporting; an abort that
 * also fails would replace a precise "could not write file" with whatever the
 * cleanup happened to raise.
 */
async function abortQuietly(writable: OpfsWritableFile): Promise<void> {
  if (typeof writable.abort !== "function") return;
  try {
    await writable.abort();
  } catch {
    /* Intentionally ignored: see above. */
  }
}

/* ------------------------------------------------------------------------- *
 * Opening the real thing
 * ------------------------------------------------------------------------- */

interface OpfsCapableStorage {
  getDirectory(): Promise<OpfsDirectoryHandle>;
}

/**
 * The origin private file system, read structurally off `globalThis`.
 *
 * A structural read rather than `navigator.storage`: this package compiles with
 * both the DOM and Node global types, where `navigator` is two different
 * things, and the presence of `getDirectory` is a runtime fact anyway — Safari
 * before 15.2 and every non-secure context have a `StorageManager` without it.
 */
function opfsStorage(): OpfsCapableStorage | undefined {
  const scope = globalThis as {
    readonly navigator?: {
      readonly storage?: { readonly getDirectory?: unknown };
    };
  };
  const storage = scope.navigator?.storage;
  if (storage === undefined || typeof storage.getDirectory !== "function") {
    return undefined;
  }
  return storage as OpfsCapableStorage;
}

/** Whether {@link openOpfsJournalFileStore} can succeed in this context. */
export function isOpfsAvailable(): boolean {
  return opfsStorage() !== undefined;
}

/**
 * The Journal's directory inside the origin private file system.
 *
 * Named rather than implied: every path in `@ff/store` is relative to the
 * Journal root, and putting the Journal in its own directory is what keeps a
 * later OPFS user from enumerating — or removing — records it does not own.
 */
export const OPFS_JOURNAL_DIRECTORY = "JournalData";

export interface OpfsJournalFileStoreOptions {
  /** Defaults to {@link OPFS_JOURNAL_DIRECTORY}. */
  readonly directoryName?: string;
}

/**
 * Opens the durable store, or throws `repositoryUnavailable`.
 *
 * `repositoryUnavailable` and not `ioFailure`: an origin with no OPFS is a
 * Journal that has nowhere to live, which is precisely the state F-JP-19's
 * unavailable banner describes, and it is not a failure a retry will fix.
 */
export async function openOpfsJournalFileStore(
  options: OpfsJournalFileStoreOptions = {},
): Promise<OpfsJournalFileStore> {
  const directoryName = options.directoryName ?? OPFS_JOURNAL_DIRECTORY;
  const storage = opfsStorage();
  if (storage === undefined) {
    throw new JournalFileError(
      "repositoryUnavailable",
      directoryName,
      "the origin private file system is not available",
    );
  }
  try {
    const origin = await storage.getDirectory();
    const root =
      directoryName.length === 0
        ? origin
        : await origin.getDirectoryHandle(directoryName, { create: true });
    return new OpfsJournalFileStore(root);
  } catch (error) {
    throw fileError(
      "repositoryUnavailable",
      directoryName,
      "could not open the origin private file system",
      error,
    );
  }
}

/**
 * Adopts a real `FileSystemDirectoryHandle` as a root.
 *
 * The cast is load-bearing and deliberate: the workspace's `lib` omits
 * `DOM.AsyncIterable`, so the standard handle type has no declared `keys()`
 * even though every implementation has one. Narrowing that mismatch here keeps
 * it out of every call site.
 */
export function adoptOpfsDirectoryHandle(handle: unknown): OpfsDirectoryHandle {
  return handle as OpfsDirectoryHandle;
}
